import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { AlertTriangle, Users, Calendar, Clock } from 'lucide-react';
import { Badge } from '../ui/badge';

interface ReservationConflict {
  status: string;
  guestNo: number;
  date: string;
  time: string;
  username: string;
}

interface TableDeleteConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tableName: string;
  hasReservations: boolean;
  reservationCount: number;
  reservationDetails: ReservationConflict[];
  onConfirmDelete: () => void;
  onCancel: () => void;
}

const TableDeleteConfirmationDialog: React.FC<TableDeleteConfirmationDialogProps> = ({
  open,
  onOpenChange,
  tableName,
  hasReservations,
  reservationCount,
  reservationDetails,
  onConfirmDelete,
  onCancel,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] bg-white dark:bg-gray-900">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            {hasReservations ? 'Cannot Delete Table' : 'Confirm Table Deletion'}
          </DialogTitle>
          <DialogDescription>
            {hasReservations 
              ? `Table "${tableName}" cannot be deleted because it has active reservations.`
              : `Are you sure you want to delete table "${tableName}"? This action cannot be undone.`
            }
          </DialogDescription>
        </DialogHeader>

        {hasReservations && reservationDetails.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Users className="w-4 h-4" />
              {reservationCount} Active Reservation{reservationCount > 1 ? 's' : ''}
            </div>
            
            <div className="max-h-60 overflow-y-auto space-y-3 border rounded-lg p-3 bg-gray-50">
              {reservationDetails.map((reservation, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <Badge 
                      variant={
                        reservation.status === 'Accepted' ? 'default' :
                        reservation.status === 'Pending' ? 'secondary' :
                        'outline'
                      }
                    >
                      {reservation.status}
                    </Badge>
                    <div className="text-sm">
                      <div className="font-medium">{reservation.username}</div>
                      <div className="text-gray-600 flex items-center gap-2">
                        <Users className="w-3 h-3" />
                        {reservation.guestNo} guests
                        <Calendar className="w-3 h-3 ml-2" />
                        {reservation.date}
                        <Clock className="w-3 h-3 ml-2" />
                        {reservation.time}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="text-blue-600 font-medium text-sm">
                  💡 What you can do:
                </div>
              </div>
              <ul className="mt-2 space-y-1 text-sm text-blue-800">
                <li>• Cancel or move the active reservations to another table</li>
                <li>• Wait for the reservations to be completed</li>
                <li>• Change reservation status to "Rejected" or "Canceled" if appropriate</li>
              </ul>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          {!hasReservations && (
            <Button variant="red" onClick={onConfirmDelete}>
              Delete Table
            </Button>
          )}
          {hasReservations && (
            <Button 
              onClick={() => {
                // Navigate to reservations page with table filter
                window.open('/reservations', '_blank');
                onCancel();
              }}
            >
              Manage Reservations
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TableDeleteConfirmationDialog;